import { translate } from '../../../i18n';

/**
 * The three ways a capacity-option write can leave the organization worse off
 * than the green toast suggests. All of them are additive, optional fields on
 * the create/update/delete responses; an API that predates any of them sends
 * nothing, which reads here as "no warning".
 */
export interface CapacityWriteWarningSource {
  /** Pricing rows keyed to a capacity this write retired — money nobody can spend. */
  strandedPricingRows?: number;
  /** Capacities this write stopped accepting WITHOUT the operator naming them. */
  retiredCapacities?: number[];
  /** Capacities that stay bookable but that no court prices — accepted, then a 404. */
  unpricedCapacities?: number[];
}

/**
 * Builds the operator-facing warning for one capacity-option write, or `null`
 * when there is nothing to say.
 *
 * Shared by the create form and the row editor because all three rails
 * (create/update/delete) can produce more than one warning from a single save,
 * and a per-call-site `if` chain had already drifted once: `update` reported
 * stranded rows while `create` reported nothing at all, and neither knew about
 * a capacity being accepted with no price behind it. Sentences are joined into
 * ONE toast rather than stacked, so a save that trips two conditions does not
 * bury the second under the first.
 *
 * Not an error, and never a reason to treat the write as failed: every one of
 * these describes a state the operator is entitled to reach, usually on the way
 * to a correct configuration (add the option, then price it). The toast tells
 * them which step is still missing.
 */
export function buildCapacityWriteWarning(
  result: CapacityWriteWarningSource | undefined | null,
): string | null {
  if (!result) {
    return null;
  }

  const sentences: string[] = [];

  const retired = result.retiredCapacities ?? [];
  if (retired.length > 0) {
    sentences.push(
      translate('game_capacity_options.retired_capacities').replace(
        /\{\{capacities\}\}/g,
        retired.join(', ')
      )
    );
  }

  const unpriced = result.unpricedCapacities ?? [];
  if (unpriced.length > 0) {
    sentences.push(
      translate('game_capacity_options.unpriced_capacities').replace(
        /\{\{capacities\}\}/g,
        unpriced.join(', ')
      )
    );
  }

  const stranded = result.strandedPricingRows ?? 0;
  if (stranded > 0) {
    sentences.push(
      translate('game_capacity_options.stranded_pricing_rows').replace(
        '{{count}}',
        String(stranded)
      )
    );
  }

  return sentences.length > 0 ? sentences.join(' ') : null;
}
